import { emailKey } from './recent-accounts'

// Sign in to another account in a new tab (the account menu, owner
// 2026-11-02, "like Google's"): this tab stays signed in to its own account.
//
// A tab keeps one session, in sessionStorage. A tab opened by script gets a
// copy of its opener's sessionStorage -- this tab's session -- unless it is
// opened without an opener, so openSignInTab always opens with noopener and
// noreferrer: the new tab starts signed out.
//
// No other account's session is ever kept (recent accounts hold no token or
// password), so the new tab runs the real sign-in for the account, exactly
// as choosing it on "Choose an account" does. The menu leaves a handoff for
// it: a localStorage entry under a random nonce, which the new tab's address
// carries (sessionStorage can't reach a tab opened without an opener). The
// sign-in page takes it once, for that email only and within
// HANDOFF_TTL_MS, and then goes on by itself. Without it -- a reload, a
// crafted or old link -- the address's email is only filled in: nobody else
// can write the entry, and a used one is gone.
export const HANDOFF_PREFIX = 'edxr-sign-in-handoff:'
export const HANDOFF_TTL_MS = 5 * 60 * 1000

const NONCE = /^[0-9a-f]{32}$/

const localStore = () => {
  try {
    return window.localStorage
  } catch (e) {
    return null
  }
}

const newNonce = () => {
  const bytes = new Uint8Array(16)
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

const isStale = (value, now) => !value || typeof value.at !== 'number' || now - value.at > HANDOFF_TTL_MS || now < value.at

// Old entries (a tab that never opened, a sign-in never finished) go
// whenever a new one is written.
const prune = (store, now) => {
  const keys = []
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (key && key.startsWith(HANDOFF_PREFIX)) keys.push(key)
  }
  keys.forEach(key => {
    let value = null
    try {
      value = JSON.parse(store.getItem(key))
    } catch (e) {}
    if (isStale(value, now)) store.removeItem(key)
  })
}

// Leaves the handoff for one email; answers its nonce, or null when storage
// refused it (the new tab then only has the email filled in).
export const noteSignInHandoff = (email, { now = Date.now() } = {}) => {
  const key = emailKey(email)
  if (!key) return null
  try {
    const store = localStore()
    if (!store) return null
    prune(store, now)
    const nonce = newNonce()
    store.setItem(`${HANDOFF_PREFIX}${nonce}`, JSON.stringify({ email: key, at: now }))
    return nonce
  } catch (e) {
    return null
  }
}

// Takes the handoff (always removing it) and answers whether it is a fresh
// one for this email.
export const takeSignInHandoff = (nonce, email, { now = Date.now() } = {}) => {
  if (typeof nonce !== 'string' || !NONCE.test(nonce)) return false
  try {
    const store = localStore()
    if (!store) return false
    const raw = store.getItem(`${HANDOFF_PREFIX}${nonce}`)
    store.removeItem(`${HANDOFF_PREFIX}${nonce}`)
    if (!raw) return false
    const value = JSON.parse(raw)
    if (isStale(value, now)) return false
    const key = emailKey(email)
    return !!key && value.email === key
  } catch (e) {
    return false
  }
}

// The sign-in page for one account (and its handoff), or for another account
// ("Add another account": the email field, as the chooser's "Use another
// account"). No redirect: an account starts on its own home page.
export const signInTabPath = (email, nonce) => {
  const params = new URLSearchParams({ email: email.trim() })
  if (nonce) params.set('handoff', nonce)
  return `/auth/email?${params.toString()}`
}

export const ADD_ACCOUNT_PATH = '/auth/email?use=another'

// A new tab with no opener: it gets no copy of this tab's session.
export const openSignInTab = path => {
  try {
    window.open(path, '_blank', 'noopener,noreferrer')
  } catch (e) {}
}
